import {
  keepPreviousData,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";

import type { HistoryResponse } from "@/components/HistoryDrawer";
import type {
  ActionOrderPayload,
  OrderDecision,
} from "@/components/LinkOrderDialog";
import type {
  LinkChangeDecision,
  LinkChangePending,
  LinkChangeReviewRow,
} from "@/components/LinkChangeReview";
import i18n from "@/lib/i18n/i18n";
import { api } from "@/lib/api/client";
import { getAccessToken } from "@/lib/auth/tokens";
import { API_ORIGIN } from "@/lib/config";
import type { components } from "@/lib/api/schema.gen";
import { unwrap } from "@/lib/api/unwrap";

import { financialAidKeys } from "./keys";
import type {
  FinancialAidActionImportReport,
  FinancialAidAdmin,
  FinancialAidCard,
  FinancialAidCatalog,
  FinancialAidDetail,
  FinancialAidFavorite,
  FinancialAidImportReport,
  TerritoryCatalog,
} from "../types";

function authHeaders(): Record<string, string> {
  const token = getAccessToken();
  const headers: Record<string, string> = {};
  if (token) headers.Authorization = `Bearer ${token}`;
  // The history API renders diff labels/badges server-side (gettext); forward
  // the active UI language so the drawer isn't stuck in the default locale.
  if (i18n.language) headers["Accept-Language"] = i18n.language;
  return headers;
}

// --- directory ---------------------------------------------------------------

/**
 * The sectors this directory's sector facet offers: the enterprise's own, read
 * from the server so the SPA applies the same rule as the Django rail
 * (`repertoire/sector_facet.py`). Refetched every time a directory mounts
 * (`staleTime: 0`, overriding the client's 30 s): the sectors can change on
 * the profile form, in the Django app or in another tab, and none of those
 * reaches this cache. A change made while a directory is open shows on its
 * next opening, not before: the client does not refetch on window focus.
 */
export function useFinancialAidDirectorySectorsQuery() {
  return useQuery({
    queryKey: financialAidKeys.directorySectors(),
    staleTime: 0,
    queryFn: () =>
      unwrap(api.GET("/api/v1/financial-aids/directory/sectors/", {})) as Promise<
        components["schemas"]["SectorRef"][]
      >,
  });
}

/** Refine facets applied server-side (parity with the Django directory). */
export interface DirectoryFilters {
  funding_types?: string[];
  service_regions?: string[];
  sectors_served?: string[];
  company_sizes_served?: string[];
  organization_types_served?: string[];
  /** The two bounds of "montant offert"; see financial_aids.directory. */
  min_amount?: number;
  max_amount?: number;
}

export function useFinancialAidDirectoryQuery(
  action: string | null,
  filters: DirectoryFilters = {},
) {
  return useQuery({
    // Filters are part of the key so a changed facet refetches (server-side).
    queryKey: [...financialAidKeys.directory(action), filters],
    // Hold the previous result while the new facet loads. Without this the fresh
    // key has no data, `isPending` flips true, and the view's loading branch
    // unmounts the whole filter rail — collapsing the facet group mid-selection.
    placeholderData: keepPreviousData,
    enabled: Boolean(action),
    // 400/403/404 (missing action, no enterprise) are meaningful states the UI
    // renders — don't retry them.
    retry: false,
    queryFn: async () => {
      const query: Record<string, unknown> = { action };
      if (filters.funding_types?.length)
        query.funding_types = filters.funding_types;
      if (filters.service_regions?.length)
        query.service_regions = filters.service_regions;
      if (filters.sectors_served?.length)
        query.sectors_served = filters.sectors_served;
      if (filters.company_sizes_served?.length)
        query.company_sizes_served = filters.company_sizes_served;
      if (filters.organization_types_served?.length)
        query.organization_types_served = filters.organization_types_served;
      if (filters.min_amount) query.min_amount = filters.min_amount;
      if (filters.max_amount) query.max_amount = filters.max_amount;
      const data = await unwrap(
        api.GET("/api/v1/financial-aids/directory/", {
          params: { query } as unknown as { query?: never },
        }),
      );
      return data as unknown as FinancialAidCard[];
    },
  });
}

export function useFinancialAidDetailQuery(action: string | null, id: string | null) {
  return useQuery({
    queryKey: financialAidKeys.detail(action, id ?? "none"),
    enabled: Boolean(action && id),
    retry: false,
    queryFn: async () => {
      const data = await unwrap(
        api.GET("/api/v1/financial-aids/directory/{id}/", {
          params: {
            path: { id: id as string },
            query: { action: action as string },
          } as unknown as { path: { id: string } },
        }),
      );
      return data as unknown as FinancialAidDetail;
    },
  });
}

// --- favorites ---------------------------------------------------------------

export function useFinancialAidFavoritesQuery(action?: string | null) {
  return useQuery({
    queryKey: financialAidKeys.favorites(action),
    retry: false,
    queryFn: async () => {
      // Scope the fetch to the action server-side (the endpoint honours
      // ?action=) so we don't download the enterprise's favorites for every
      // other action. Unpaginated (pagination_class = None) → a plain array.
      const data = await unwrap(
        api.GET("/api/v1/financial-aids/favorites/", {
          params: action
            ? ({ query: { action } } as unknown as { query?: never })
            : {},
        }),
      );
      return data as unknown as FinancialAidFavorite[];
    },
  });
}

export function useAddFinancialAidFavorite() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { action: string; aidId: string }) => {
      const body = {
        action: input.action,
        financial_aid: input.aidId,
      } as components["schemas"]["FinancialAidFavorite"];
      return unwrap(api.POST("/api/v1/financial-aids/favorites/", { body }));
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: financialAidKeys.all });
    },
  });
}

export function useRemoveFinancialAidFavorite() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (favoriteId: string) =>
      unwrap(
        api.DELETE("/api/v1/financial-aids/favorites/{id}/", {
          params: { path: { id: favoriteId } },
        }),
      ),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: financialAidKeys.all });
    },
  });
}

// --- CQDD back-office (admin) ------------------------------------------------

/** Action catalogue for the picker (system actions + enjeu/objectif lists).
 * The endpoint is an untyped APIView, so this fetches with the bearer token. */
export function useFinancialAidActionCatalog(enabled = true) {
  return useQuery({
    queryKey: financialAidKeys.adminCatalog(),
    enabled,
    staleTime: 60 * 60 * 1000,
    queryFn: async (): Promise<FinancialAidCatalog> => {
      const res = await fetch(
        `${API_ORIGIN}/api/v1/financial-aids/admin/action-catalog/`,
        { headers: authHeaders() },
      );
      if (!res.ok) throw new Error("HTTP " + res.status);
      return (await res.json()) as FinancialAidCatalog;
    },
  });
}

/**
 * The Québec MRC catalogue behind the territory picker, grouped by region.
 *
 * Served rather than duplicated here: a territory only reaches an enterprise
 * when both sides spell the MRC the way the province's layer does, so the list
 * has exactly one home (`core_project/territories.py`). It never changes within
 * a session, hence the long staleTime.
 */
export function useTerritoryCatalog(enabled = true) {
  return useQuery({
    queryKey: financialAidKeys.adminTerritories(),
    enabled,
    staleTime: 60 * 60 * 1000,
    queryFn: async () => {
      const data = await unwrap(
        api.GET("/api/v1/financial-aids/admin/territories/", {}),
      );
      return (data as unknown as TerritoryCatalog).regions;
    },
  });
}

export function useAdminFinancialAidsQuery() {
  return useQuery({
    queryKey: financialAidKeys.adminList(),
    queryFn: async () => {
      // Unpaginated (pagination_class = None) → a plain array, not an envelope.
      const data = await unwrap(
        api.GET("/api/v1/financial-aids/admin/financial-aids/", {}),
      );
      return data as unknown as FinancialAidAdmin[];
    },
  });
}

export function useCreateFinancialAid() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: Partial<FinancialAidAdmin>) =>
      unwrap(
        api.POST("/api/v1/financial-aids/admin/financial-aids/", {
          body: body as components["schemas"]["FinancialAidAdmin"],
        }),
      ),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: financialAidKeys.admin() });
    },
  });
}

export function useUpdateFinancialAid() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { id: string; body: Partial<FinancialAidAdmin> }) =>
      unwrap(
        api.PUT("/api/v1/financial-aids/admin/financial-aids/{id}/", {
          params: { path: { id: input.id } },
          body: input.body as components["schemas"]["FinancialAidAdmin"],
        }),
      ),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: financialAidKeys.admin() });
    },
  });
}

export function useDeleteFinancialAid() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      unwrap(
        api.DELETE("/api/v1/financial-aids/admin/financial-aids/{id}/", {
          params: { path: { id } },
        }),
      ),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: financialAidKeys.admin() });
    },
  });
}

/** One aid's change history for the drawer (untyped APIView → fetch + token). */
export function useFinancialAidHistory(id: string | null) {
  return useQuery({
    // Language is part of the key: the server renders labels per Accept-Language,
    // so a language switch must refetch rather than serve a stale-locale cache.
    queryKey: [...financialAidKeys.adminHistory(id), i18n.language],
    enabled: Boolean(id),
    queryFn: async (): Promise<HistoryResponse> => {
      const res = await fetch(
        `${API_ORIGIN}/api/v1/financial-aids/admin/financial-aids/${id}/history/`,
        { headers: authHeaders() },
      );
      if (!res.ok) throw new Error("HTTP " + res.status);
      return (await res.json()) as HistoryResponse;
    },
  });
}

/** Bulk .xlsx aid import (multipart). The import APIView is untyped in the
 * schema, so this posts with FormData + bearer token directly. */
export function useImportFinancialAids() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (file: File): Promise<FinancialAidImportReport> => {
      const form = new FormData();
      form.append("file", file);
      const res = await fetch(
        `${API_ORIGIN}/api/v1/financial-aids/admin/financial-aids/import/`,
        { method: "POST", headers: authHeaders(), body: form },
      );
      const data = await res.json();
      if (!res.ok) throw data;
      return data as FinancialAidImportReport;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: financialAidKeys.admin() });
    },
  });
}

/** Bulk aid↔action association import (multipart). One (aid_title, action_id)
 * per row; row order sets display order and a listed aid's link set is replaced. */
export function useImportFinancialAidActions() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (file: File): Promise<FinancialAidActionImportReport> => {
      const form = new FormData();
      form.append("file", file);
      const res = await fetch(
        `${API_ORIGIN}/api/v1/financial-aids/admin/financial-aid-actions/import/`,
        { method: "POST", headers: authHeaders(), body: form },
      );
      const data = await res.json();
      if (!res.ok) throw data;
      return data as FinancialAidActionImportReport;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: financialAidKeys.admin() });
    },
  });
}


// --- per-action aid order (four-eyes) --------------------------------------

/** The action's current aid order + any staged proposal. */
export function useActionFinancialAidOrder(actionId: string) {
  return useQuery({
    queryKey: financialAidKeys.adminActionOrder(actionId),
    retry: false,
    queryFn: async () => {
      const data = await unwrap(
        api.GET("/api/v1/financial-aids/admin/actions/{action_id}/order/", {
          params: { path: { action_id: actionId } },
        }),
      );
      return data as unknown as ActionOrderPayload;
    },
  });
}

/** Propose / approve / counter-propose an order — the server enforces four-eyes. */
export function useActionFinancialAidOrderDecision(actionId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (decision: OrderDecision) => {
      const data = await unwrap(
        api.POST("/api/v1/financial-aids/admin/actions/{action_id}/order/", {
          params: { path: { action_id: actionId } },
          body: decision as components["schemas"]["ActionOrderDecision"],
        }),
      );
      return data as unknown as ActionOrderPayload;
    },
    onSuccess: (data) => {
      qc.setQueryData(financialAidKeys.adminActionOrder(actionId), data);
      // An approved order changes what enterprises see in the directory.
      void qc.invalidateQueries({ queryKey: financialAidKeys.all });
    },
  });
}


// --- link-set review (four-eyes) ---------------------------------------------

/** The financial aids whose link change awaits *your* validation (the list banner). */
export function useFinancialAidLinkChangesQuery() {
  return useQuery({
    queryKey: financialAidKeys.adminLinkChanges(),
    retry: false,
    queryFn: async () => {
      const data = await unwrap(api.GET("/api/v1/financial-aids/admin/link-changes/", {}));
      return data as unknown as LinkChangeReviewRow[];
    },
  });
}

/** Approve several pending link changes at once — your own are skipped. */
export function useApproveFinancialAidLinkChanges() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (requestIds: string[]) =>
      unwrap(
        api.POST("/api/v1/financial-aids/admin/link-changes/", {
          body: {
            request_ids: requestIds,
          } as components["schemas"]["LinkSetBatchDecision"],
        }),
      ),
    onSuccess: () => {
      // Approving writes the live links, so the whole slice is stale: the
      // directory shows different financial aids on the pistes d'action involved.
      void qc.invalidateQueries({ queryKey: financialAidKeys.all });
    },
  });
}

/** One resource's open link change, or ``null``. */
export function useFinancialAidLinkChange(id: string | null) {
  return useQuery({
    queryKey: financialAidKeys.adminLinkChange(id ?? "none"),
    enabled: Boolean(id),
    retry: false,
    queryFn: async () => {
      const data = await unwrap(
        api.GET("/api/v1/financial-aids/admin/financial-aids/{id}/link-change/", {
          params: { path: { id: id as string } },
        }),
      );
      return ((data as { pending?: LinkChangePending } | undefined)?.pending ??
        null) as LinkChangePending | null;
    },
  });
}

/** Approve / counter-propose one link change — the server enforces four-eyes. */
export function useFinancialAidLinkChangeDecision(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (decision: LinkChangeDecision) => {
      const data = await unwrap(
        api.POST("/api/v1/financial-aids/admin/financial-aids/{id}/link-change/", {
          params: { path: { id } },
          body: decision as components["schemas"]["LinkSetDecision"],
        }),
      );
      return ((data as { pending?: LinkChangePending } | undefined)?.pending ??
        null) as LinkChangePending | null;
    },
    onSuccess: (data) => {
      qc.setQueryData(financialAidKeys.adminLinkChange(id), data);
      void qc.invalidateQueries({ queryKey: financialAidKeys.all });
    },
  });
}
